/**
 * Asset Preview Component
 *
 * Renders a preview of an asset based on its media type.
 * Supports image, video, audio, and a placeholder fallback.
 * Reusable across AssetDetailPanel, AssetView, and other components.
 */

import { lazy, Suspense } from 'react';
import { MediaType } from '../types';

const ModelViewer = lazy(() => import('./ModelViewer'));
const PanoramaViewer = lazy(() => import('./PanoramaViewer'));


export interface AssetPreviewProps {
  /** URL of the preview media */
  previewUrl: string;
  /** Media type of the asset */
  mediaType: MediaType;
  /** Asset name (used for alt text) */
  name: string;
  /** URL for 3D model preview (if applicable) */
  modelSrc?: string;
  /** CSS class for the image/video element */
  className?: string;
  /** CSS class for the placeholder element */
  placeholderClassName?: string;
  /** CSS class for the audio element */
  audioClassName?: string;
  hideControls?: boolean;
  isInteractive?: boolean;
  /** Embed code for the asset */
  embedCode?: string;
  /** Whether to show a preview for embedded content */
  showEmbedPreview?: boolean;
  /** Environment image URL for ModelViewer */
  environmentImage?: string;
}

export const AssetPreview = ({
  previewUrl,
  mediaType,
  name,
  modelSrc,
  className = 'asset-detail-panel__preview-image',
  placeholderClassName = 'asset-detail-panel__preview-placeholder',
  audioClassName = 'asset-detail-panel__preview-audio',
  hideControls = false,
  isInteractive = false,
  embedCode,
  showEmbedPreview = false,
  environmentImage,
}: AssetPreviewProps) => {

  const renderPreview = () => {
    if (embedCode && showEmbedPreview) {
      return (
        <div className={className} dangerouslySetInnerHTML={{ __html: embedCode }} />
      )
    }
    if (!previewUrl) {
      return (
        <div className={placeholderClassName}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
        </div>
      );
    }
    
    if (isInteractive) {
      if (mediaType === MediaType.Model3D) {
        return (
          <Suspense fallback={<div className={placeholderClassName} />}>
            <ModelViewer
              src={modelSrc || previewUrl}
              alt={name}
              poster={previewUrl}
              hideControls={hideControls}
              environmentImage={environmentImage}
            />
          </Suspense>
        )
      }

      if (mediaType === MediaType.Image360 || mediaType === MediaType.Video360) {
        return (
          <Suspense fallback={<div className={placeholderClassName} />}>
            <PanoramaViewer
              src={previewUrl}
              isVideo={mediaType === MediaType.Video360}
              caption={name}
            />
          </Suspense>
        );
      }
    }

    if (mediaType === MediaType.Video || mediaType === MediaType.Video360) {
      return (
        <video
          src={previewUrl}
          controls
          className={className}
          preload="metadata"
        >
          Your browser does not support the video element.
        </video>
      );
    }

    if (mediaType === MediaType.Audio) {
      return (
        <audio
          src={previewUrl}
          controls
          className={audioClassName}
          preload="metadata"
        >
          Your browser does not support the audio element.
        </audio>
      );
    }


    return (
      <img
        src={previewUrl}
        alt={name}
        className={className}
      />
    );
  }

  return (
    <>
      {renderPreview()}
    </>
  )
};
